import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [isValidSession, setIsValidSession] = useState(false);
  const [isInvite, setIsInvite] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10;

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        setIsValidSession(true);
        setChecking(false);

        const hash = window.location.hash;
        if (hash.includes('type=invite') || hash.includes('type=signup')) {
          setIsInvite(true);
        }
        return;
      }

      retryCount++;
      if (retryCount < maxRetries) {
        setTimeout(checkSession, 500);
      } else {
        toast.error('Ungültiger oder abgelaufener Link. Bitte fordern Sie einen neuen an.');
        setChecking(false);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    checkSession();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      toast.success(isInvite
        ? 'Passwort gesetzt! Sie werden zum Dashboard weitergeleitet...'
        : 'Passwort erfolgreich zurückgesetzt!'
      );

      setTimeout(() => navigate('/'), 1500);
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Setzen des Passworts');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-neutral-600">Link wird überprüft...</p>
        </div>
      </div>
    );
  }

  if (!isValidSession) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">Link ungültig</h2>
          <p className="text-neutral-600 mb-4">
            Dieser Link ist abgelaufen oder bereits verwendet worden.
            Sie werden zum Login weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-blue-100/50 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-purple-100/50 via-transparent to-transparent rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/25">
            <span className="text-white font-bold text-2xl">NE</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isInvite ? 'Willkommen! Passwort setzen' : 'Neues Passwort setzen'}
          </h1>
          <p className="text-gray-500 mt-2">
            {isInvite
              ? 'Legen Sie Ihr Passwort fest, um auf Ihr Dashboard zuzugreifen.'
              : 'Geben Sie Ihr neues Passwort ein.'}
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {isInvite ? 'Passwort wählen' : 'Neues Passwort'}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                disabled={loading}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 text-sm
                         placeholder:text-gray-400 transition-all duration-200
                         focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500
                         hover:border-gray-400"
              />
              <p className="mt-1.5 text-xs text-gray-500">Mindestens 6 Zeichen</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Passwort bestätigen
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                disabled={loading}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 text-sm
                         placeholder:text-gray-400 transition-all duration-200
                         focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500
                         hover:border-gray-400"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !password || !confirmPassword}
              className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl
                       hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2
                       disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200
                       shadow-lg shadow-blue-600/20"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Wird gespeichert...
                </span>
              ) : (
                isInvite ? 'Passwort setzen & loslegen' : 'Passwort speichern'
              )}
            </button>

            {!isInvite && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-gray-500 hover:text-gray-700 transition"
                  disabled={loading}
                >
                  Zurück zum Login
                </button>
              </div>
            )}
          </form>
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          © {new Date().getFullYear()} Nico Els. Alle Rechte vorbehalten.
        </p>
      </div>
    </div>
  );
}
